import { useCallback, useState, type FormEvent } from 'react';
import { Modal } from '../../components/Modal';
import { TicketHead } from '../../components/TicketHead';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatTimestamp, versionLetter } from '../../lib/format';
import type { AdminFlagRead, FlagStatus } from '../../types/api';
import { FactCheckPanel } from '../review/FactCheckPanel';
import { fetchAdminFlags, resolveAdminFlag } from './adminApi';
import { usePagedList } from './usePagedList';

/** Mirrors the backend's `MAX_FLAG_REASON_LENGTH` — notes are read, not parsed. */
const NOTE_MAX_LENGTH = 2000;

const FILTERS: readonly { status: FlagStatus; label: string }[] = [
  { status: 'open', label: 'Open' },
  { status: 'resolved', label: 'Resolved' },
  { status: 'dismissed', label: 'Dismissed' },
];

type Resolution = 'resolved' | 'dismissed';

function flagId(flag: AdminFlagRead): string {
  return flag.id;
}

/**
 * The optional note that closes a flag.
 *
 * Optional because the honest answer is often "nothing wrong here", and forcing
 * a sentence out of an operator produces filler rather than a record.
 */
function ResolveDialog({
  resolution,
  isBusy,
  error,
  onSubmit,
  onDismiss,
}: {
  resolution: Resolution;
  isBusy: boolean;
  error: string | null;
  onSubmit: (note: string | null) => void;
  onDismiss: () => void;
}) {
  const [note, setNote] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isBusy) return;
    onSubmit(note.trim() === '' ? null : note.trim());
  }

  return (
    <Modal
      title={resolution === 'resolved' ? 'Resolve this flag' : 'Dismiss this flag'}
      kind="flag"
      onDismiss={onDismiss}
    >
      <p className="modal__message">
        {resolution === 'resolved'
          ? 'Records that the complaint was upheld. The draft itself is untouched.'
          : 'Records that there was nothing wrong with the flagged draft.'}{' '}
        Closing is final — a flag leaves the queue exactly once.
      </p>

      <form className="flag-form" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field__label">Note (optional)</span>
          <textarea
            className="field__input"
            rows={4}
            maxLength={NOTE_MAX_LENGTH}
            autoFocus
            value={note}
            disabled={isBusy}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="modal__actions">
          <button className="button" type="button" onClick={onDismiss} disabled={isBusy}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={isBusy}>
            {isBusy ? 'Saving' : resolution === 'resolved' ? 'Resolve' : 'Dismiss'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * One report, with the copy that was reported frozen inside it.
 *
 * The snapshot is what an operator judges: the live draft may have been
 * regenerated, layer-edited or superseded since, and a queue that showed the
 * current rows would stop describing the complaint.
 */
function FlagCard({
  flag,
  onClosed,
}: {
  flag: AdminFlagRead;
  onClosed: (closed: AdminFlagRead, wasAlreadyClosed: boolean) => void;
}) {
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const snapshot = flag.snapshot;
  const isOpen = flag.status === 'open';

  function submit(note: string | null) {
    if (resolution === null || isBusy) return;
    setIsBusy(true);
    setError(null);

    void (async () => {
      try {
        const closed = await resolveAdminFlag(flag.id, { resolution, note });
        setIsBusy(false);
        setResolution(null);
        onClosed(closed, false);
      } catch (caught) {
        setIsBusy(false);
        if (caught instanceof ApiError && caught.code === 'FLAG_ALREADY_RESOLVED') {
          setResolution(null);
          // Another operator got there first. Their verdict stands — this row is
          // simply no longer in the open queue.
          onClosed(flag, true);
          return;
        }
        setError(getErrorMessage(caught, 'That did not save. Try again.'));
      }
    })();
  }

  return (
    <li className="flag-card">
      <TicketHead label={`Flag / ${flag.id.slice(0, 8)}`} timestamp={flag.created_at} />

      <div className="flag-card__head">
        <h3 className="flag-card__business">
          {flag.business_name || flag.business_id.slice(0, 8)}
        </h3>
        <span className={`status-chip status-chip--${isOpen ? 'waiting' : 'closed'}`}>
          {flag.status}
        </span>
      </div>

      <p className="flag-card__reason">{flag.reason}</p>

      <div className="flag-card__snapshot">
        <p className="flag-card__label">What was flagged</p>
        {snapshot.request ? (
          <p className="flag-card__meta">
            {snapshot.request.post_type ?? 'unknown type'} · {snapshot.request.platform ?? '—'} ·{' '}
            {snapshot.request.status ?? '—'}
            {snapshot.variant_index !== null
              ? ` · version ${versionLetter(snapshot.variant_index)}`
              : ''}
          </p>
        ) : null}
        <p className="flag-card__caption">{snapshot.caption ?? 'No caption in this snapshot.'}</p>
        {snapshot.slides.length > 0 ? (
          <ul className="flag-card__slides">
            {snapshot.slides.map((slide, index) => (
              <li key={`${slide.slide_index}-${index}`} className="flag-card__slide">
                <span className="flag-card__slide-index">{slide.slide_index + 1}</span>
                <span>{slide.headline ?? '—'}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <FactCheckPanel
          factCheck={snapshot.fact_check}
          lede="The advisory verdicts as the reviewer saw them, frozen at the moment of the report."
        />
      </div>

      {isOpen ? (
        <div className="flag-card__actions">
          <button className="button" type="button" onClick={() => setResolution('resolved')}>
            Resolve
          </button>
          <button className="button" type="button" onClick={() => setResolution('dismissed')}>
            Dismiss
          </button>
        </div>
      ) : (
        <p className="flag-card__closed">
          {flag.status === 'resolved' ? 'Resolved' : 'Dismissed'}
          {flag.resolved_by_email ? ` by ${flag.resolved_by_email}` : ''}
          {flag.resolved_at ? ` on ${formatTimestamp(flag.resolved_at)}` : ''}
          {flag.resolution_note ? ` — ${flag.resolution_note}` : ''}
        </p>
      )}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {resolution ? (
        <ResolveDialog
          resolution={resolution}
          isBusy={isBusy}
          error={error}
          onSubmit={submit}
          onDismiss={() => setResolution(null)}
        />
      ) : null}
    </li>
  );
}

/** One page of the queue for a single status. Remounted when the filter moves. */
function FlagList({ status }: { status: FlagStatus }) {
  const fetchPage = useCallback(
    (page: { limit: number; offset: number }, signal: AbortSignal) =>
      fetchAdminFlags(status, page, signal),
    [status],
  );
  const list = usePagedList(fetchPage, flagId);
  const [note, setNote] = useState<string | null>(null);

  const handleClosed = useCallback(
    (closed: AdminFlagRead, wasAlreadyClosed: boolean) => {
      setNote(wasAlreadyClosed ? 'Another operator had already closed that one.' : null);
      // A closed flag is no longer in the open queue; on the closed filters the
      // row stays and reports its new verdict.
      if (status === 'open') {
        list.remove(closed.id);
        // The page is short by one now, so re-read it rather than showing 19.
        list.retry();
        return;
      }
      list.replace(closed);
    },
    [list, status],
  );

  return (
    <>
      {note ? (
        <p className="form__notice" role="status">
          {note}
        </p>
      ) : null}

      {list.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading flags
        </p>
      ) : list.items.length === 0 && !list.error ? (
        <p className="admin-empty">
          {status === 'open' ? 'Nothing waiting. The queue is clear.' : `No ${status} flags.`}
        </p>
      ) : (
        <ul className="flag-list">
          {list.items.map((flag) => (
            <FlagCard key={flag.id} flag={flag} onClosed={handleClosed} />
          ))}
        </ul>
      )}

      {list.error ? (
        <div className="admin-pane__foot">
          <p className="form__error" role="alert">
            {list.error}
          </p>
          <button className="button" type="button" onClick={list.retry}>
            Try again
          </button>
        </div>
      ) : list.hasMore ? (
        <div className="admin-pane__foot">
          <button
            className="button"
            type="button"
            onClick={list.loadMore}
            disabled={list.isLoadingMore}
          >
            {list.isLoadingMore ? 'Loading' : 'Load more'}
          </button>
        </div>
      ) : null}
    </>
  );
}

/**
 * The flag queue: what members reported, newest first.
 *
 * Resolving one records a verdict and nothing else. There is no control here
 * that edits, withdraws or regenerates a customer's draft — a flag is a report
 * to staff, and staff answering it must not be able to reach into the work.
 */
export function AdminFlagQueue() {
  const [status, setStatus] = useState<FlagStatus>('open');

  return (
    <div className="admin-pane">
      <div className="tabs" role="tablist" aria-label="Flag status">
        {FILTERS.map((filter) => (
          <button
            key={filter.status}
            className={filter.status === status ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
            type="button"
            role="tab"
            aria-selected={filter.status === status}
            onClick={() => setStatus(filter.status)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <FlagList key={status} status={status} />
    </div>
  );
}
